// frontend/src/components/PowerBI/PowerBISubscriptions.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { powerbiAPI } from '../../api/client';
import { FiPlus, FiEdit2, FiTrash2, FiPlay, FiClock, FiCheckCircle,
         FiAlertTriangle, FiX, FiRefreshCw, FiShield, FiCopy } from 'react-icons/fi';
import SubscriptionScheduler from './SubscriptionScheduler';
import { schedulerToCron, cronToScheduler } from '../../utils/schedulerToCron';
import './PowerBISubscriptions.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUS_META = {
  success: { label: 'Sucesso',     color: '#059669', bg: '#d1fae5' },
  failed:  { label: 'Erro',        color: '#dc2626', bg: '#fee2e2' },
  running: { label: 'Em execução', color: '#2563eb', bg: '#dbeafe' },
  null:    { label: 'Nunca exec.', color: '#9ca3af', bg: '#f3f4f6' },
};
const fmt     = d => d ? new Date(d).toLocaleString('pt-PT')    : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('pt-PT') : '—';

const DEFAULT_SCHEDULE = {
  repeat: 'daily', hour: '08', minute: '00',
  weekdays: ['MON'], monthDayType: 'specific', monthDay: 1,
  timezone: 'Europe/Lisbon',
};

const EMPTY = {
  name: '', description: '',
  group_id: '', report_id: '', page_name: '', page_display: '',
  export_format: 'PDF',
  cron_expression: schedulerToCron(DEFAULT_SCHEDULE),
  timezone: 'Europe/Lisbon',
  is_active: true,
  email_subject: 'Relatório: {{report_name}} — {{run_date}}',
  email_body: '<p>Em anexo o relatório <strong>{{report_name}}</strong> de {{run_date}}.</p>',
  recipients: [],
};

// ── Auth Banner ───────────────────────────────────────────────────────────────
const AuthBanner = ({ onAuthComplete }) => {
  const [status,   setStatus]   = useState('checking'); // checking|ok|needed|authing|done|error
  const [authData, setAuthData] = useState(null);       // { user_code, verification_uri, device_code, interval }
  const [copied,   setCopied]   = useState(false);
  const [seconds,  setSeconds]  = useState(0);
  const pollRef  = useRef(null);
  const timerRef = useRef(null);

  const checkStatus = useCallback(async () => {
    try {
      const r = await powerbiAPI.getAuthStatus();
      setStatus(r.data.authenticated ? 'ok' : 'needed');
    } catch {
      setStatus('needed');
    }
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  // Countdown timer while code is displayed
  useEffect(() => {
    if (status !== 'authing' || !authData) return;
    setSeconds(authData.expires_in || 900);
    timerRef.current = setInterval(() => setSeconds(s => s <= 1 ? 0 : s - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [status, authData]);

  const startAuth = async () => {
    setStatus('authing');
    try {
      const r = await powerbiAPI.initDeviceAuth();
      setAuthData(r.data);
      // Poll every `interval` seconds (Azure mandates minimum 5s)
      const intervalMs = Math.max((r.data.interval || 5), 5) * 1000;
      pollRef.current = setInterval(() => pollAuth(r.data.device_code), intervalMs);
    } catch (err) {
      setStatus('error');
      console.error('Auth init failed:', err);
    }
  };

  const pollAuth = async (deviceCode) => {
    try {
      const r = await powerbiAPI.pollDeviceAuth({ device_code: deviceCode });
      if (r.data.status === 'success') {
        clearInterval(pollRef.current);
        clearInterval(timerRef.current);
        setStatus('done');
        onAuthComplete?.();
      } else if (r.data.status === 'expired' || r.data.status === 'declined') {
        clearInterval(pollRef.current);
        setStatus('needed');
        setAuthData(null);
      }
      // 'pending' → keep polling
    } catch {
      clearInterval(pollRef.current);
      setStatus('error');
    }
  };

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(authData?.user_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fmtSeconds = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (status === 'checking' || status === 'ok') return null;

  if (status === 'done') return (
    <div className="pbi-auth-banner pbi-auth-banner--success">
      <FiCheckCircle size={16}/>
      <span>Autenticação concluída com sucesso. A sessão renova automaticamente.</span>
    </div>
  );

  if (status === 'authing' && authData) return (
    <div className="pbi-auth-banner pbi-auth-banner--authing">
      <div className="pbi-auth-step">
        <FiShield size={18} style={{ color: 'var(--primary)', flexShrink: 0 }}/>
        <div>
          <strong>Autenticação Power BI necessária</strong>
          <p>
            Abra{' '}
            <a href={authData.verification_uri} target="_blank" rel="noopener noreferrer">
              {authData.verification_uri}
            </a>
            {' '}e introduza o código abaixo. Pode usar MFA normalmente.
          </p>
          <div className="pbi-device-code-row">
            <span className="pbi-device-code">{authData.user_code}</span>
            <button type="button" className="pbi-copy-btn" onClick={copyCode} title="Copiar código">
              <FiCopy size={13}/> {copied ? 'Copiado!' : 'Copiar'}
            </button>
            {seconds > 0 && (
              <span className="pbi-code-timer">expira em {fmtSeconds(seconds)}</span>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 6 }}>
            <FiRefreshCw size={11} className="spin" style={{ marginRight: 4 }}/>
            A aguardar confirmação…
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pbi-auth-banner pbi-auth-banner--warning">
      <FiAlertTriangle size={16}/>
      <span>
        {status === 'error'
          ? 'Erro ao iniciar autenticação. Tente novamente.'
          : 'Conta Power BI não autenticada. É necessário autenticar para exportar relatórios.'}
      </span>
      <button className="btn-primary btn-sm pbi-auth-btn" onClick={startAuth}>
        <FiShield size={13}/> Autenticar agora
      </button>
    </div>
  );
};

// ── Subscription Modal ────────────────────────────────────────────────────────
const SubscriptionModal = ({ sub, onSave, onClose }) => {
  const [form,       setForm]       = useState(sub ? { ...sub } : { ...EMPTY });
  const [schedState, setSchedState] = useState(() => ({
    ...DEFAULT_SCHEDULE,
    ...(sub?.cron_expression ? cronToScheduler(sub.cron_expression) : {}),
    timezone: sub?.timezone || DEFAULT_SCHEDULE.timezone,
  }));
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [newEmail,   setNewEmail]   = useState('');
  const [newName,    setNewName]    = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [reports,    setReports]    = useState([]);
  const [pages,      setPages]      = useState([]);
  const [loadingPBI, setLoadingPBI] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [sending,    setSending]    = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    powerbiAPI.getWorkspaces()
      .then(r => setWorkspaces(r.data.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.group_id) { setReports([]); setPages([]); return; }
    setLoadingPBI('reports');
    powerbiAPI.getWorkspaceReports(form.group_id)
      .then(r => setReports(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoadingPBI(''));
  }, [form.group_id]);

  useEffect(() => {
    if (!form.group_id || !form.report_id) { setPages([]); return; }
    setLoadingPBI('pages');
    powerbiAPI.getReportPages(form.group_id, form.report_id)
      .then(r => setPages(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoadingPBI(''));
  }, [form.group_id, form.report_id]);

  const handleScheduleChange = (data) => {
    setSchedState(data);
    setF('cron_expression', schedulerToCron(data));
    setF('timezone', data.timezone);
  };

  const addRecipient = () => {
    if (!newEmail || !newEmail.includes('@')) return;
    if (form.recipients.find(r => r.email === newEmail)) return;
    setF('recipients', [...form.recipients, { email: newEmail, name: newName }]);
    setNewEmail(''); setNewName('');
  };

  const removeRecipient = (email) =>
    setF('recipients', form.recipients.filter(r => r.email !== email));

  const handleSendNow = async () => {
    if (!sub?.id) return;
    setSending(true); setSendResult(null);
    try {
      await powerbiAPI.runNow(sub.id);
      setSendResult({ ok: true, msg: 'Exportação iniciada — o email será enviado em breve.' });
    } catch (err) {
      setSendResult({ ok: false, msg: err.response?.data?.error || 'Erro ao iniciar exportação.' });
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.group_id || !form.report_id || !form.cron_expression) {
      setError('Nome, workspace, relatório e agendamento são obrigatórios.');
      return;
    }
    setSaving(true); setError('');
    try { await onSave(form); }
    catch (err) { setError(err.response?.data?.error || 'Erro ao guardar'); setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content pbi-modal">
        <div className="modal-header">
          <h3>{sub ? 'Editar Subscrição' : 'Nova Subscrição Power BI'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>×</button>
        </div>

        <div className="modal-form pbi-form">
          <form id="pbi-sub-form" onSubmit={handleSubmit}>
            {error && <div className="form-error">⚠️ {error}</div>}

            <div className="form-row form-row-2">
              <div className="form-group">
                <label>Nome *</label>
                <input className="form-control" value={form.name}
                  onChange={e => setF('name', e.target.value)} placeholder="ex: Vendas Semanais" />
              </div>
              <div className="form-group">
                <label>Formato</label>
                <div className="format-pill-group">
                  {['PDF', 'PNG', 'PPTX'].map(f => (
                    <button key={f} type="button"
                      className={`format-pill ${form.export_format === f ? 'active' : ''}`}
                      onClick={() => setF('export_format', f)}>
                      {f}
                    </button>
                  ))}
                </div>
                {(form.export_format === 'PNG' || form.export_format === 'PPTX') && (
                  <p style={{ fontSize: 11, color: '#b45309', marginTop: 5 }}>
                    ⚠️ {form.export_format} requer workspace com capacidade Premium ou PPU.
                  </p>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Descrição</label>
              <input className="form-control" value={form.description || ''}
                onChange={e => setF('description', e.target.value)} />
            </div>

            {/* Power BI selectors */}
            <div className="pbi-section-title">
              📊 Relatório Power BI
              <button type="button" onClick={() => setManualMode(m => !m)}
                style={{ marginLeft: 10, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                  background: manualMode ? '#e0f2f1' : 'var(--gray-100)',
                  color: manualMode ? 'var(--primary)' : 'var(--gray-500)',
                  border: '1px solid', borderColor: manualMode ? 'var(--primary)' : 'var(--gray-300)',
                  borderRadius: 10, padding: '1px 8px', verticalAlign: 'middle' }}>
                {manualMode ? '⬅ usar dropdowns' : '✏ inserir IDs manualmente'}
              </button>
            </div>

            {manualMode ? (
              <div>
                <p style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 10, lineHeight: 1.6 }}>
                  Cole os IDs a partir do URL do relatório:<br/>
                  <code style={{ background: 'var(--gray-100)', padding: '1px 4px', borderRadius: 3 }}>
                    app.powerbi.com/groups/<strong>WORKSPACE_ID</strong>/reports/<strong>REPORT_ID</strong>/ReportSectionXXX
                  </code>
                </p>
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label>Workspace ID *</label>
                    <input className="form-control" value={form.group_id}
                      onChange={e => setF('group_id', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      style={{ fontFamily: 'monospace', fontSize: 12 }} />
                  </div>
                  <div className="form-group">
                    <label>Report ID *</label>
                    <input className="form-control" value={form.report_id}
                      onChange={e => setF('report_id', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      style={{ fontFamily: 'monospace', fontSize: 12 }} />
                  </div>
                </div>
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label>Nome interno da Página <span style={{ fontWeight:400, color:'var(--gray-400)', fontSize:11 }}>(opcional)</span></label>
                    <input className="form-control" value={form.page_name || ''}
                      onChange={e => setF('page_name', e.target.value)}
                      placeholder="ex: ReportSection2a4b…"
                      style={{ fontFamily: 'monospace', fontSize: 12 }} />
                  </div>
                  <div className="form-group">
                    <label>Nome visível da Página</label>
                    <input className="form-control" value={form.page_display || ''}
                      onChange={e => setF('page_display', e.target.value)}
                      placeholder="ex: Vendas por Região" />
                  </div>
                </div>
                {form.group_id && form.report_id && (
                  <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 4 }}
                    disabled={loadingPBI === 'pages'}
                    onClick={() => {
                      setLoadingPBI('pages');
                      powerbiAPI.getReportPages(form.group_id, form.report_id)
                        .then(r => setPages(r.data.data || []))
                        .catch(() => {})
                        .finally(() => setLoadingPBI(''));
                    }}>
                    {loadingPBI === 'pages' ? 'A carregar…' : '🔍 Carregar páginas disponíveis'}
                  </button>
                )}
                {pages.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <p style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>Selecione uma página:</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {pages.map(p => (
                        <button key={p.name} type="button"
                          onClick={() => { setF('page_name', p.name); setF('page_display', p.displayName); }}
                          style={{ padding: '3px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12,
                            fontWeight: form.page_name === p.name ? 700 : 400,
                            border: `1.5px solid ${form.page_name === p.name ? 'var(--primary)' : 'var(--gray-300)'}`,
                            background: form.page_name === p.name ? '#e0f2f1' : 'white',
                            color: form.page_name === p.name ? 'var(--primary)' : 'var(--gray-600)' }}>
                          {p.displayName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {workspaces.length === 0 && (
                  <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 6, fontSize: 12, color: '#92400e', marginBottom: 10 }}>
                    ⚠️ Nenhum workspace disponível. Certifique-se de que está autenticado ou use{' '}
                    <button type="button" onClick={() => setManualMode(true)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--primary)', textDecoration: 'underline', fontSize: 12 }}>
                      inserção manual de IDs
                    </button>.
                  </div>
                )}
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label>Workspace *</label>
                    <select className="form-control" value={form.group_id}
                      onChange={e => { setF('group_id', e.target.value); setF('report_id', ''); setF('page_name', ''); }}>
                      <option value="">— selecionar —</option>
                      {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Relatório *</label>
                    <select className="form-control" value={form.report_id}
                      onChange={e => { setF('report_id', e.target.value); setF('page_name', ''); }}
                      disabled={!form.group_id || loadingPBI === 'reports'}>
                      <option value="">— selecionar —</option>
                      {reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Página <span style={{ fontWeight:400, color:'var(--gray-400)', fontSize:11 }}>(opcional)</span></label>
                    <select className="form-control" value={form.page_name || ''}
                      onChange={e => {
                        const p = pages.find(p => p.name === e.target.value);
                        setF('page_name', e.target.value);
                        setF('page_display', p?.displayName || '');
                      }}
                      disabled={!form.report_id || loadingPBI === 'pages'}>
                      <option value="">Todas as páginas</option>
                      {pages.map(p => <option key={p.name} value={p.name}>{p.displayName}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule */}
            <div className="pbi-section-title">⏰ Agendamento</div>
            <SubscriptionScheduler value={schedState} onChange={handleScheduleChange} />
            <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
              Expressão gerada:{' '}
              <code style={{ fontFamily: 'monospace', background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 3 }}>
                {form.cron_expression}
              </code>{' '}·{' '}
              <a href="https://crontab.guru" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>
                verificar
              </a>
            </p>
            <div style={{ marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: 500, color: 'var(--gray-700)' }}>
                <input type="checkbox" checked={form.is_active}
                  onChange={e => setF('is_active', e.target.checked)} />
                Subscrição activa
              </label>
            </div>

            {/* Email */}
            <div className="pbi-section-title">📧 Email</div>
            <div className="form-group">
              <label>Assunto</label>
              <input className="form-control" value={form.email_subject || ''}
                onChange={e => setF('email_subject', e.target.value)}
                placeholder="{{report_name}} — {{run_date}}" />
            </div>
            <div className="form-group">
              <label>Corpo <span style={{ fontWeight:400, color:'var(--gray-400)', fontSize:11 }}>(HTML)</span></label>
              <textarea className="form-control" rows={3} value={form.email_body || ''}
                onChange={e => setF('email_body', e.target.value)} />
              <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 3 }}>
                Variáveis: <code>{'{{report_name}}'}</code> <code>{'{{run_date}}'}</code> <code>{'{{full_name}}'}</code>
              </p>
            </div>

            {/* Recipients */}
            <div className="pbi-section-title">👥 Destinatários</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 10 }}>
              <input className="form-control" placeholder="email@empresa.pt"
                value={newEmail} onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addRecipient())} />
              <input className="form-control" placeholder="Nome (opcional)"
                value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addRecipient())} />
              <button type="button" className="btn-primary btn-sm" onClick={addRecipient}
                disabled={!newEmail.includes('@')}>
                <FiPlus size={14}/>
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {form.recipients.map(r => (
                <span key={r.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', background: '#e0f2f1', borderRadius: 20,
                  fontSize: 12, color: 'var(--primary)' }}>
                  {r.name ? `${r.name} <${r.email}>` : r.email}
                  <button type="button" onClick={() => removeRecipient(r.email)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', display: 'flex' }}>
                    <FiX size={11}/>
                  </button>
                </span>
              ))}
              {form.recipients.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--gray-400)', fontStyle: 'italic' }}>Nenhum destinatário</span>
              )}
            </div>
          </form>
        </div>

        {sendResult && (
          <div className={`form-${sendResult.ok ? 'success-bar' : 'error'}`}
            style={{ margin: '0 24px 4px', fontSize: 12 }}>
            {sendResult.ok ? '✅' : '⚠️'} {sendResult.msg}
          </div>
        )}

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          {sub && (
            <button type="button" className="btn-send-now" disabled={sending || saving} onClick={handleSendNow}>
              {sending ? <><FiRefreshCw size={14} className="spin"/> A enviar…</> : <><FiPlay size={14}/> Enviar agora</>}
            </button>
          )}
          <button type="submit" form="pbi-sub-form" className="btn-primary" disabled={saving}>
            {saving ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Run History ───────────────────────────────────────────────────────────────
const RunHistory = ({ subId, onClose }) => {
  const [runs, setRuns]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    powerbiAPI.getRunHistory(subId)
      .then(r => setRuns(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subId]);

  return (
    <div className="pbi-history-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 14 }}>Histórico de Execuções</h4>
        <button type="button" onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
          <FiX size={15}/>
        </button>
      </div>
      {loading
        ? <p style={{ fontSize: 13, color: 'var(--gray-400)' }}>A carregar…</p>
        : runs.length === 0
          ? <p style={{ fontSize: 13, color: 'var(--gray-400)', fontStyle: 'italic' }}>Sem execuções registadas.</p>
          : runs.map(r => {
              const sm = STATUS_META[r.status] || STATUS_META.null;
              return (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 12 }}>
                  <div>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                      background: sm.bg, color: sm.color, fontWeight: 600, fontSize: 11 }}>
                      {sm.label}
                    </span>
                    {r.error_text && (
                      <span style={{ marginLeft: 8, color: '#dc2626', fontSize: 11 }} title={r.error_text}>
                        ⚠️ {r.error_text.slice(0, 60)}
                      </span>
                    )}
                  </div>
                  <div style={{ color: 'var(--gray-500)', textAlign: 'right' }}>
                    <div>{fmt(r.started_at)}</div>
                    {r.recipients_count > 0 && (
                      <div style={{ fontSize: 11 }}>
                        {r.recipients_count} dest.{r.file_size_bytes ? ` · ${(r.file_size_bytes/1024).toFixed(0)} KB` : ''}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
      }
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const PowerBISubscriptions = () => {
  const [subs,       setSubs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null);
  const [historyId,  setHistoryId]  = useState(null);
  const [runningIds, setRunningIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try { setSubs((await powerbiAPI.listSubscriptions()).data.data || []); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (modal === 'new') await powerbiAPI.createSubscription(form);
    else                 await powerbiAPI.updateSubscription(modal.id, form);
    setModal(null);
    load();
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Eliminar subscrição "${sub.name}"?`)) return;
    await powerbiAPI.deleteSubscription(sub.id);
    load();
  };

  const handleRunNow = async (sub) => {
    setRunningIds(s => new Set([...s, sub.id]));
    try {
      await powerbiAPI.runNow(sub.id);
      setTimeout(() => {
        setRunningIds(s => { const n = new Set(s); n.delete(sub.id); return n; });
        load();
      }, 8000);
    } catch {
      setRunningIds(s => { const n = new Set(s); n.delete(sub.id); return n; });
    }
  };

  return (
    <div className="pbi-page">
      <div className="page-header page-header--compact">
        <h2>Power BI — Subscrições</h2>
        <div className="page-header-actions">
          <button className="btn btn-secondary btn-sm" onClick={load} title="Atualizar">
            <FiRefreshCw size={14}/>
          </button>
          <button className="btn-primary" onClick={() => setModal('new')}>
            <FiPlus size={15} style={{ marginRight: 6 }}/> Nova Subscrição
          </button>
        </div>
      </div>

      {/* Auth banner — shows only when not authenticated */}
      <AuthBanner onAuthComplete={load} />

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--gray-400)' }}>A carregar…</div>
      ) : subs.length === 0 ? (
        <div className="pbi-empty">
          <p>📊 Nenhuma subscrição configurada.</p>
          <button className="btn-primary" onClick={() => setModal('new')}>
            <FiPlus size={14}/> Criar primeira subscrição
          </button>
        </div>
      ) : (
        <div className="pbi-list">
          {subs.map(sub => {
            const sm        = STATUS_META[sub.last_run_status] || STATUS_META.null;
            const isRunning = runningIds.has(sub.id);
            return (
              <div key={sub.id} className={`pbi-card ${!sub.is_active ? 'pbi-card--inactive' : ''}`}>
                <div className="pbi-card-header">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="pbi-card-name">{sub.name}</span>
                      <span className="pbi-format-badge">{sub.export_format}</span>
                      {!sub.is_active && (
                        <span style={{ fontSize: 11, color: 'var(--gray-400)',
                          padding: '1px 7px', background: 'var(--gray-100)', borderRadius: 10, fontWeight: 600 }}>
                          Inactiva
                        </span>
                      )}
                    </div>
                    {sub.description && (
                      <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '2px 0 0' }}>{sub.description}</p>
                    )}
                  </div>
                  <div className="pbi-card-actions">
                    <button className="as-icon-btn" title="Executar agora" disabled={isRunning} onClick={() => handleRunNow(sub)}>
                      {isRunning ? <FiRefreshCw size={14} className="spin"/> : <FiPlay size={14}/>}
                    </button>
                    <button className="as-icon-btn" title="Histórico"
                      onClick={() => setHistoryId(historyId === sub.id ? null : sub.id)}>
                      <FiClock size={14}/>
                    </button>
                    <button className="as-icon-btn" onClick={() => setModal(sub)}><FiEdit2 size={14}/></button>
                    <button className="as-icon-btn as-icon-btn-danger" onClick={() => handleDelete(sub)}><FiTrash2 size={14}/></button>
                  </div>
                </div>
                <div className="pbi-card-meta">
                  <span>⏰ <code style={{ fontSize: 11 }}>{sub.cron_expression}</code></span>
                  {sub.page_display && <span>📄 {sub.page_display}</span>}
                  <span>📧 {(sub.recipients || []).length} destinatário(s)</span>
                  {sub.next_run_at && <span>▶ Próxima: {fmtDate(sub.next_run_at)}</span>}
                </div>
                <div className="pbi-card-footer">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 11, padding: '2px 8px', borderRadius: 10, background: sm.bg, color: sm.color, fontWeight: 600 }}>
                    {sub.last_run_status === 'success' ? <FiCheckCircle size={11}/> :
                     sub.last_run_status === 'failed'  ? <FiAlertTriangle size={11}/> : null}
                    {sm.label}
                  </span>
                  {sub.last_run_at && (
                    <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                      Última execução: {fmt(sub.last_run_at)}
                    </span>
                  )}
                  {sub.last_run_error && (
                    <span style={{ fontSize: 11, color: '#dc2626' }} title={sub.last_run_error}>
                      ⚠️ {sub.last_run_error.slice(0, 80)}
                    </span>
                  )}
                </div>
                {historyId === sub.id && <RunHistory subId={sub.id} onClose={() => setHistoryId(null)} />}
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <SubscriptionModal
          sub={modal === 'new' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default PowerBISubscriptions;